import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Brain, X, Plus, Pencil, Trash2, Check, Loader2, Sparkles } from "lucide-react";

/**
 * "What your coach remembers about you" — a per-coach memory drawer.
 * Auto-extracted facts are marked with a Sparkles icon; manual ones with a user icon.
 */
export function CoachMemoryDrawer({ coach, coachLabel, onClose }) {
  const [facts, setFacts] = useState(null);
  const [adding, setAdding] = useState("");
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null); // {id, text}

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/coach/memory", { params: { coach } });
      setFacts(data.facts || []);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  }, [coach]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    const text = adding.trim();
    if (!text) return;
    setSaving(true);
    try {
      const { data } = await api.post("/coach/memory", { coach, text, category: "manual" });
      setFacts((p) => [data.fact, ...(p || [])]);
      setAdding("");
      toast.success("Added to memory.");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const saveEdit = async () => {
    if (!editing?.text.trim()) return;
    try {
      await api.put(`/coach/memory/${editing.id}`, { text: editing.text });
      setFacts((p) => p.map((f) => f.id === editing.id ? { ...f, text: editing.text } : f));
      setEditing(null);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this from your coach's memory?")) return;
    try {
      await api.delete(`/coach/memory/${id}`);
      setFacts((p) => p.filter((f) => f.id !== id));
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return createPortal(
    <AnimatePresence>
      <motion.aside initial={{ x: 420, opacity: 0 }} animate={{ x: 0, opacity: 1 }} exit={{ x: 420, opacity: 0 }}
        transition={{ type: "spring", stiffness: 300, damping: 28 }}
        className="fixed bottom-0 right-0 top-0 z-[85] flex w-full max-w-md flex-col border-l border-[#FFE8E3] bg-white shadow-warm-lg"
        data-testid="memory-drawer">
        <div className="flex items-start justify-between border-b border-[#FFE8E3] p-5">
          <div>
            <p className="flex items-center gap-2 font-nunito text-sm font-bold text-[#C2410C]">
              <Brain className="h-4 w-4" strokeWidth={2.5} /> What your coach remembers
            </p>
            <p className="mt-0.5 font-quicksand text-xs text-[#718096]">{coachLabel} · builds automatically as you chat</p>
          </div>
          <button onClick={onClose} data-testid="memory-close-btn" className="rounded-full bg-[#FFF9F5] p-1.5 text-[#718096] hover:bg-[#FFE8E3]"><X className="h-4 w-4" /></button>
        </div>

        <div className="flex-1 space-y-2 overflow-y-auto p-4" data-testid="memory-list">
          {facts === null ? (
            <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-[#FF7E67]" /></div>
          ) : facts.length === 0 ? (
            <div className="rounded-2xl border-2 border-dashed border-[#FFE8E3] p-6 text-center">
              <Sparkles className="mx-auto mb-2 h-6 w-6 text-[#FFB347]" />
              <p className="font-nunito text-sm font-bold text-[#2D3748]">Nothing remembered yet.</p>
              <p className="mt-1 font-quicksand text-xs text-[#718096]">Chat with your coach and durable details will appear here. You can also add facts manually below.</p>
            </div>
          ) : (
            facts.map((f) => (
              <div key={f.id} data-testid={`memory-fact-${f.id}`}
                className="group rounded-2xl border border-[#FFE8E3] bg-[#FFF9F5] p-3 transition-colors hover:border-[#FF7E67]">
                <div className="flex items-start gap-2">
                  <span className={`mt-0.5 shrink-0 rounded-full px-2 py-0.5 font-nunito text-[10px] font-bold uppercase tracking-wider ${
                    f.source === "manual" ? "bg-[#E6FBF5] text-[#06D6A0]" : "bg-[#FFE8E3] text-[#C2410C]"}`}>
                    {f.source === "manual" ? "You" : "Auto"}
                  </span>
                  {editing?.id === f.id ? (
                    <div className="flex-1 space-y-2">
                      <textarea value={editing.text} onChange={(e) => setEditing({ ...editing, text: e.target.value })}
                        data-testid={`memory-edit-input-${f.id}`} rows={2}
                        className="w-full resize-none rounded-xl border border-[#FFE8E3] bg-white px-3 py-2 font-quicksand text-sm outline-none focus:border-[#FF7E67]" />
                      <div className="flex gap-2">
                        <button onClick={saveEdit} data-testid={`memory-save-${f.id}`}
                          className="inline-flex items-center gap-1 rounded-full bg-sunrise px-3 py-1 font-nunito text-xs font-bold text-white">
                          <Check className="h-3 w-3" /> Save
                        </button>
                        <button onClick={() => setEditing(null)}
                          className="rounded-full border border-[#FFE8E3] px-3 py-1 font-nunito text-xs font-bold text-[#718096]">
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <p className="flex-1 font-quicksand text-sm text-[#2D3748]">{f.text}</p>
                      <div className="flex shrink-0 opacity-0 transition-opacity group-hover:opacity-100">
                        <button onClick={() => setEditing({ id: f.id, text: f.text })}
                          data-testid={`memory-edit-${f.id}`}
                          className="rounded-full p-1.5 text-[#718096] hover:bg-white hover:text-[#C2410C]" title="Edit"><Pencil className="h-3.5 w-3.5" /></button>
                        <button onClick={() => remove(f.id)}
                          data-testid={`memory-delete-${f.id}`}
                          className="rounded-full p-1.5 text-[#718096] hover:bg-white hover:text-[#B91C1C]" title="Remove"><Trash2 className="h-3.5 w-3.5" /></button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        <div className="flex items-center gap-2 border-t border-[#FFE8E3] p-4">
          <input value={adding} onChange={(e) => setAdding(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()}
            placeholder="Add something you want the coach to remember…"
            data-testid="memory-add-input"
            className="h-11 flex-1 rounded-full border border-[#FFE8E3] bg-[#FFF9F5] px-4 font-quicksand text-sm outline-none focus:border-[#FF7E67]" />
          <button onClick={add} disabled={saving || !adding.trim()} data-testid="memory-add-btn"
            className="flex h-11 w-11 items-center justify-center rounded-full bg-sunrise text-white shadow-glow-coral disabled:opacity-60">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
          </button>
        </div>
      </motion.aside>
    </AnimatePresence>,
    document.body
  );
}
